import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { PageHead, Skel } from '../components/ui'
import { thaiDateTime } from '../lib/format'

interface FlagRow {
  key: string
  description: string
  enabled: boolean
  rollout_pct: number
  updated_by?: string
  updated_at?: string
}

interface Draft {
  enabled: boolean
  rollout_pct: number
}

const QUERY_KEY = ['feature-flags']

export function FeatureFlags() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: QUERY_KEY, queryFn: () => api<FlagRow[]>('/v1/feature-flags') })
  const [draft, setDraft] = useState<Record<string, Draft>>({})
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (v: { key: string; enabled: boolean; rollout_pct: number }) =>
      apiWrite<FlagRow>(`/v1/feature-flags/${encodeURIComponent(v.key)}`, 'PATCH', {
        enabled: v.enabled,
        rollout_pct: v.rollout_pct,
      }),
    onSuccess: (row, v) => {
      // Write the saved row into the cache before dropping the draft, otherwise the old value
      // flashes back while the list refetches.
      qc.setQueryData<FlagRow[]>(QUERY_KEY, (old) => old
        ? old.map((x) => (x.key === row.key ? row : x))
        : [row])
      setDraft((prev) => {
        const next = { ...prev }
        delete next[v.key]
        return next
      })
      setError(null)
      void qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: (e, v) => {
      setError(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ')
      setDraft((prev) => {
        const next = { ...prev }
        delete next[v.key]
        return next
      })
    },
    onSettled: () => setSavingKey(null),
  })

  const rows = q.data ?? []
  const active = rows.filter((f) => (draft[f.key] ?? f).enabled).length
  const busy = save.isPending

  function valueOf(f: FlagRow): Draft {
    return draft[f.key] ?? { enabled: f.enabled, rollout_pct: f.rollout_pct }
  }

  function commit(f: FlagRow, next: Draft) {
    if (busy) return
    setDraft((prev) => ({ ...prev, [f.key]: next }))
    setSavingKey(f.key)
    save.mutate({ key: f.key, enabled: next.enabled, rollout_pct: next.rollout_pct })
  }

  function toggle(f: FlagRow, on: boolean) {
    const current = valueOf(f)
    // An enabled flag at 0% would be on for nobody; switching on opens it to everyone first.
    const rollout = on && current.rollout_pct === 0 ? 100 : current.rollout_pct
    commit(f, { enabled: on, rollout_pct: rollout })
  }

  function slide(f: FlagRow, value: number, commitNow: boolean) {
    if (!commitNow) {
      const current = valueOf(f)
      setDraft((prev) => ({ ...prev, [f.key]: { ...current, rollout_pct: value } }))
      return
    }
    // The drag has already written the draft; compare with the server value so releasing the
    // handle actually saves instead of looking like a no-op.
    if (busy) return
    if (value === f.rollout_pct && !draft[f.key]) return
    const current = valueOf(f)
    commit(f, { enabled: current.enabled, rollout_pct: value })
  }

  return (
    <>
      <PageHead
        title="ฟีเจอร์แฟล็ก"
        sub="เปิดหรือปิดความสามารถแบบเรียลไทม์ — มีผลกับคำขอถัดไปโดยไม่ต้อง deploy ใหม่"
        actions={
          <span className="chip">
            เปิดใช้งาน {active} จาก {rows.length}
          </span>
        }
      />

      {q.isLoading ? (
        <div className="stack"><Skel h={56} /><Skel h={56} /><Skel h={56} /></div>
      ) : q.isError ? (
        <div className="card card-pad">
          <div className="form-error">โหลดฟีเจอร์แฟล็กไม่สำเร็จ — ลองรีเฟรชหน้าอีกครั้ง</div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 74 }}>สถานะ</th>
                <th>ฟีเจอร์</th>
                <th style={{ width: 230 }}>การเปิดให้ใช้</th>
                <th style={{ width: 180 }}>อัปเดตล่าสุด</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => {
                const v = valueOf(f)
                const pending = busy && savingKey === f.key
                return (
                  <tr key={f.key} style={{ cursor: 'default' }}>
                    <td>
                      <label className="row" style={{ gap: 0, cursor: busy ? 'wait' : 'pointer' }}>
                        <span style={{ position: 'relative', display: 'inline-flex', width: 36, height: 20, flex: 'none' }}>
                          <input
                            type="checkbox"
                            role="switch"
                            aria-label={`เปิดใช้งาน ${f.key}`}
                            checked={v.enabled}
                            disabled={busy}
                            onChange={(e) => toggle(f, e.target.checked)}
                            style={{
                              position: 'absolute', inset: 0, margin: 0, appearance: 'none', WebkitAppearance: 'none',
                              background: v.enabled ? 'var(--color-gold)' : 'var(--color-hairline-strong)',
                              border: `1px solid ${v.enabled ? 'var(--color-gold)' : 'var(--color-hairline-strong)'}`,
                              borderRadius: 'var(--radius-full)',
                              cursor: busy ? 'wait' : 'pointer',
                              opacity: busy && !pending ? 0.55 : 1,
                              transition: 'background var(--micro) var(--ease-standard)',
                            }}
                          />
                          <span
                            aria-hidden="true"
                            style={{
                              position: 'absolute', top: 2, left: v.enabled ? 18 : 2, width: 16, height: 16,
                              borderRadius: '50%', pointerEvents: 'none',
                              background: v.enabled ? '#1A1712' : 'var(--color-ivory-dim)',
                              transition: 'left var(--micro) var(--ease-standard)',
                            }}
                          />
                        </span>
                      </label>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 6 }}>
                        {pending ? 'กำลังบันทึก…' : v.enabled ? 'เปิด' : 'ปิด'}
                      </div>
                    </td>
                    <td>
                      <div style={{ color: 'var(--color-ivory)' }}>{f.description}</div>
                      <div className="mono dim" style={{ fontSize: 11, marginTop: 3 }}>{f.key}</div>
                    </td>
                    <td>
                      <div className="row" style={{ gap: 10 }}>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={1}
                          value={v.rollout_pct}
                          disabled={busy}
                          aria-label={`เปอร์เซ็นต์การเปิดใช้ ${f.key}`}
                          onChange={(e) => slide(f, Number(e.target.value), false)}
                          onPointerUp={(e) => slide(f, Number((e.target as HTMLInputElement).value), true)}
                          onKeyUp={(e) => slide(f, Number((e.target as HTMLInputElement).value), true)}
                          onBlur={(e) => slide(f, Number(e.target.value), true)}
                          style={{ width: 132, accentColor: 'var(--color-gold)', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}
                        />
                        <span className="tnum" style={{ minWidth: 40, textAlign: 'right' }}>{v.rollout_pct}%</span>
                      </div>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 5 }}>
                        เปิดให้ผู้ใช้ตามสัดส่วน (แบ่งกลุ่มด้วย hash ที่คงที่)
                      </div>
                    </td>
                    <td className="dim" style={{ fontSize: 11.5 }}>
                      {f.updated_at ? thaiDateTime(f.updated_at) : '—'}
                      {f.updated_by ? <div className="mono" style={{ fontSize: 10, marginTop: 3 }}>{f.updated_by.slice(0, 8)}</div> : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {error ? <div className="form-error" style={{ marginTop: 14 }}>{error}</div> : null}

      <div className="card card-pad" style={{ marginTop: 22 }}>
        <div className="card-label">การทำงานของฟีเจอร์แฟล็ก</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>บันทึกทันทีเมื่อสลับสวิตช์หรือปล่อยแถบเลื่อน — ไม่ต้องรอ deploy หรือรีสตาร์ตบริการ</li>
          <li>ค่าเริ่มต้นของแต่ละองค์กรถูกสร้างครั้งแรกที่เปิดหน้านี้ และจะไม่ถูกเขียนทับอีก</li>
          <li>เปอร์เซ็นต์การเปิดใช้คำนวณจาก hash ของผู้ใช้แบบ FNV-1a ผู้ใช้คนเดิมจึงได้ผลเหมือนเดิมทุกครั้ง</li>
          <li>ทุกการเปลี่ยนแปลงถูกบันทึกใน audit trail พร้อมผู้แก้ไขและเวลา</li>
        </ul>
      </div>
    </>
  )
}
